import { createRouter, createWebHistory } from 'vue-router'
import { installBackCloses } from '@/lib/backCloses.js'
import { createOpenIdAuthRoutes, rememberIntendedPath, takeIntendedPath } from '@addictivegroup/addictive-auth'
import { usePanelAppStore, usePermissionsStore } from '@addictivegroup/addictive-permissions'
import { NetworkError } from '@addictivegroup/addictive-offline'
import i18n from '@/i18n/index.js'
import { useSession } from '@/stores/session.js'
import { useMeta } from '@/stores/meta.js'
import { PANEL_PAGES, ENTITY_PAGES, firstAllowedRoute } from '@/lib/panelPages.js'
import { entityByPath } from '@/lib/entities.js'

/**
 * Routes. Sign-in screens come from addictive-auth; the callback lives at /signed-in because
 * /auth/* belongs to Core (OIDC_FRONTEND_CALLBACK_PATH). Everything else needs a person:
 * online from Core, offline from the copy kept on the device (D-35).
 */
const CALLBACK_PATH = '/signed-in'
const t = (key) => i18n.global.t(key)
const ENTITY_PATHS = 'accounts|contacts|leads|opportunities|activities|notes|offers|competitors|campaigns|marketing-lists|events|contracts|agreements|sales-materials'

const authRoutes = createOpenIdAuthRoutes().map((route) => {
  const common = { ...route, meta: { ...route.meta, public: true, bare: true } }
  if (route.name === 'openid-callback') {
    return {
      ...common,
      path: CALLBACK_PATH,
      meta: { public: true, bare: true },
      props: () => ({
        successRedirect: takeIntendedPath() || '/',
        messages: { title: t('auth.callback.title'), processing: t('auth.callback.processing'), success: t('auth.callback.success'), failed: t('auth.callback.failed') },
      }),
    }
  }
  if (route.name === 'openid-login') {
    return { ...common, props: () => ({ messages: { title: t('auth.login.title'), subtitle: t('auth.login.subtitle'), buttonLabel: t('auth.login.buttonLabel') } }) }
  }
  return {
    ...common,
    props: () => ({ messages: { title: t('auth.signOut.title'), processing: t('auth.signOut.processing'), handingOver: t('auth.signOut.handingOver'), failed: t('auth.signOut.failed'), retry: t('auth.signOut.retry') } }),
  }
})

const settings = (path, name, component, page) => ({ path, name: `settings-${name}`, component, meta: { pageUuid: page, settings: true } })

const routes = [
  ...authRoutes,
  { path: '/', name: 'dashboard', component: () => import('@/views/DashboardView.vue'), meta: { pageUuid: PANEL_PAGES.Dashboard } },
  { path: '/work', name: 'work', component: () => import('@/views/WorkView.vue'), meta: { pageUuid: PANEL_PAGES.Work } },
  { path: '/search', name: 'search', component: () => import('@/views/SearchView.vue'), meta: { pageUuid: PANEL_PAGES.Search } },
  { path: '/reports', name: 'reports', component: () => import('@/views/ReportsView.vue'), meta: { pageUuid: PANEL_PAGES.Reports } },
  { path: '/files/subscriptions/:uuid', name: 'subscription-file', component: () => import('@/views/SubscriptionFileView.vue'), props: true, meta: { pageUuid: PANEL_PAGES.Reports } },
  { path: '/sync', name: 'sync', component: () => import('@/views/SyncView.vue') },
  // A notification's button (D-257): done here, as the person signed in.
  { path: '/n/:token', name: 'notification-action', component: () => import('@/views/NotificationActionView.vue') },
  { path: '/me', name: 'profile', component: () => import('@/views/ProfileView.vue'), meta: { pageUuid: PANEL_PAGES.Profile } },
  { path: '/offers/:uuid/editor', name: 'offer-editor', component: () => import('@/views/offers/OfferEditorView.vue'), props: true, meta: { pageUuid: PANEL_PAGES.Offers, wide: true } },
  { path: `/:entityPath(${ENTITY_PATHS})`, name: 'list', component: () => import('@/views/EntityListView.vue'), props: true },
  { path: `/:entityPath(${ENTITY_PATHS})/new`, name: 'record-new', component: () => import('@/views/RecordView.vue'), props: true },
  { path: `/:entityPath(${ENTITY_PATHS})/:uuid`, name: 'record', component: () => import('@/views/RecordView.vue'), props: true },
  { path: '/catalogue', name: 'catalogue', component: () => import('@/views/catalogue/CatalogueView.vue'), meta: { pageUuid: PANEL_PAGES.Catalogue } },
  // A catalogue carried to another environment or company: exported, imported through a look first.
  { path: '/catalogue/package', name: 'catalogue-package', component: () => import('@/views/catalogue/CataloguePackageView.vue'), meta: { pageUuid: PANEL_PAGES.Catalogue } },
  { path: '/catalogue/templates', name: 'templates', component: () => import('@/views/catalogue/TemplatesView.vue'), meta: { pageUuid: PANEL_PAGES.OfferTemplates } },
  // Every element of the catalogue on a page of its own (D-163); "new" for one being made.
  { path: '/catalogue/templates/:uuid', name: 'template', component: () => import('@/views/catalogue/TemplateEditorView.vue'), props: true, meta: { pageUuid: PANEL_PAGES.OfferTemplates } },
  // D-324: a product whole, built on one page.
  { path: '/catalogue/products/:uuid/map', name: 'catalogue-product-map', component: () => import('@/views/catalogue/CatalogueProductMapView.vue'), props: true, meta: { pageUuid: PANEL_PAGES.Catalogue } },
  { path: '/catalogue/products/:uuid', name: 'catalogue-product', component: () => import('@/views/catalogue/CatalogueProductView.vue'), props: true, meta: { pageUuid: PANEL_PAGES.Catalogue } },
  { path: '/catalogue/modules/:uuid', name: 'catalogue-module', component: () => import('@/views/catalogue/CatalogueModuleView.vue'), props: true, meta: { pageUuid: PANEL_PAGES.Catalogue } },
  { path: '/catalogue/components/:uuid', name: 'catalogue-component', component: () => import('@/views/catalogue/CatalogueComponentView.vue'), props: true, meta: { pageUuid: PANEL_PAGES.Catalogue } },
  { path: '/materials', name: 'materials', component: () => import('@/views/MaterialsView.vue') },
  { path: '/catalogue/documents/:uuid', name: 'catalogue-document', component: () => import('@/views/catalogue/CatalogueDocumentView.vue'), props: true, meta: { pageUuid: PANEL_PAGES.Catalogue } },
  { path: '/catalogue/sections/:uuid', name: 'catalogue-section', component: () => import('@/views/catalogue/CatalogueSectionView.vue'), props: true, meta: { pageUuid: PANEL_PAGES.Catalogue } },
  { path: '/catalogue/types/:kind(section-types|table-types|summary-types)/:uuid', name: 'catalogue-type', component: () => import('@/views/catalogue/CatalogueTypeView.vue'), props: true, meta: { pageUuid: PANEL_PAGES.Catalogue } },
  { path: '/catalogue/:object(sla-tables|coverage-tariffs|price-lists|terms-sets|module-areas|product-categories|offer-traits)/:uuid', name: 'catalogue-config', component: () => import('@/views/catalogue/CatalogueConfigView.vue'), props: true, meta: { pageUuid: PANEL_PAGES.Catalogue } },
  {
    path: '/settings',
    component: () => import('@/views/settings/SettingsLayout.vue'),
    children: [
      { path: '', name: 'settings', component: () => import('@/views/settings/SettingsHome.vue') },
      settings('general', 'general', () => import('@/views/settings/GeneralSettings.vue'), PANEL_PAGES.SettingsGeneral),
      settings('pipelines', 'pipelines', () => import('@/views/settings/PipelinesSettings.vue'), PANEL_PAGES.SettingsPipelines),
      settings('fields', 'fields', () => import('@/views/settings/FieldsSettings.vue'), PANEL_PAGES.SettingsFields),
      settings('dictionaries', 'dictionaries', () => import('@/views/settings/DictionariesSettings.vue'), PANEL_PAGES.SettingsDictionaries),
      settings('speech', 'speech', () => import('@/views/settings/SpeechSettings.vue'), PANEL_PAGES.SettingsSpeech),
      settings('ai', 'ai', () => import('@/views/settings/AiSettings.vue'), PANEL_PAGES.SettingsAi),
      settings('catalogue-naming', 'catalogue-naming', () => import('@/views/settings/CatalogueNamingSettings.vue'), PANEL_PAGES.SettingsCatalogueNaming),
      settings('numbering', 'numbering', () => import('@/views/settings/NumberingSettings.vue'), PANEL_PAGES.SettingsNumbering),
      settings('currencies', 'currencies', () => import('@/views/settings/CurrenciesSettings.vue'), PANEL_PAGES.SettingsCurrencies),
      settings('contracts', 'contracts', () => import('@/views/settings/ContractsSettings.vue'), PANEL_PAGES.SettingsContracts),
      settings('approvals', 'approvals', () => import('@/views/settings/ApprovalsSettings.vue'), PANEL_PAGES.SettingsApprovals),
      settings('tags', 'tags', () => import('@/views/settings/TagsSettings.vue'), PANEL_PAGES.SettingsTags),
      settings('duplicates', 'duplicates', () => import('@/views/settings/DuplicatesSettings.vue'), PANEL_PAGES.SettingsDuplicates),
      settings('assignment', 'assignment', () => import('@/views/settings/AssignmentSettings.vue'), PANEL_PAGES.SettingsAssignment),
      settings('sequences', 'sequences', () => import('@/views/settings/SequencesSettings.vue'), PANEL_PAGES.SettingsSequences),
      settings('views', 'views', () => import('@/views/settings/ViewsSettings.vue'), PANEL_PAGES.SettingsViews),
      settings('branding', 'branding', () => import('@/views/settings/BrandingSettings.vue'), PANEL_PAGES.SettingsBranding),
      settings('offline', 'offline', () => import('@/views/settings/OfflineSettings.vue'), PANEL_PAGES.SettingsOffline),
      settings('devices', 'devices', () => import('@/views/settings/DevicesSettings.vue'), PANEL_PAGES.SettingsDevices),
      settings('audit', 'audit', () => import('@/views/settings/AuditSettings.vue'), PANEL_PAGES.SettingsAudit),
      settings('data', 'data', () => import('@/views/settings/DataSettings.vue'), PANEL_PAGES.SettingsData),
    ],
  },
  { path: '/forbidden', name: 'forbidden', component: () => import('@/views/NotFoundView.vue'), props: { forbidden: true } },
  { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('@/views/NotFoundView.vue') },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: (to, from, saved) => saved || (to.hash ? { el: to.hash } : { top: 0 }),
})

// Back on a phone closes the panel or dialog on top first (before any other guard).
installBackCloses(router)

router.beforeEach(async (to) => {
  if (to.meta.public) return true
  const session = useSession()
  try {
    await session.load()
  } catch (error) {
    if (!(error instanceof NetworkError)) rememberIntendedPath(to.fullPath)
    return { name: 'openid-login', query: { redirect: to.fullPath } }
  }
  const meta = useMeta()
  await meta.load().catch(() => null)
  const pages = usePanelAppStore()
  await Promise.all([pages.loadMyPages().catch(() => null), usePermissionsStore().load?.().catch(() => null)])

  const entity = to.params.entityPath ? entityByPath(String(to.params.entityPath)) : null
  const page = to.meta.pageUuid || (entity ? ENTITY_PAGES[entity] : null)
  if (page && pages.loaded && !pages.canViewPage(page)) {
    const start = firstAllowedRoute((p) => pages.canViewPage(p))
    if (to.name === 'dashboard' && start) return start
    return { name: 'forbidden', query: start ? { from: to.fullPath } : { none: '1' } }
  }
  return true
})

export default router
